import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { StatusCodes } from 'http-status-codes';
import { useHistory, useLocation } from 'react-router-dom';
import { Col, Form } from 'react-bootstrap';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { LocationFormik, LocationFormikToLocationDto, QuantityGetDto } from 'svila-common';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import LocationEditModal from '../../../../components/common/LocationEditModal';
import ProductBox from '../../../../components/common/product/ProductBox';
import InventoryApi from '../../../../services/api/InventoryApi';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import { locationSchema } from '../InventorySchema';

const Arrow = styled.div`
  margin-top: 30px;
`;

export default function InventoryMove(): JSX.Element {
  const [products, setProducts] = useState<QuantityGetDto[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingFrom, setIsSubmittingFrom] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const chamberNumber = params.get('chamberNumber');
  const column = params.get('column');
  const letter = params.get('letter');
  const level = params.get('level');

  const loadProducts = async (values: LocationFormik): Promise<void> => {
    setIsSubmittingFrom(true);
    try {
      const res = await InventoryApi.getQuantitiesByLocation(
        values.chamberNumber,
        values.column,
        values.letter,
        values.level,
      );
      if (res.status === StatusCodes.OK) {
        setProducts(res.data);
      }
    } finally {
      setIsSubmittingFrom(false);
    }
  };

  const onClose = async (values: LocationFormik): Promise<void> => {
    setIsSubmittingFrom(true);
    const res = await InventoryApi.getQuantitiesByLocation(
      values.chamberNumber,
      values.column,
      values.letter,
      values.level,
    );

    if (res.status === StatusCodes.OK) {
      setProducts(res.data);
    }
    setIsSubmittingFrom(false);
  };

  const formik1 = useFormik({
    initialValues: {
      initialValues: {
        chamberNumber: chamberNumber ?? '',
        column: column ?? '',
        letter: letter ?? '',
        level: level ?? '',
      },
    },
    validationSchema: locationSchema,

    onSubmit: (values, setSubmitting) => {
      return onClose(values, setSubmitting);
    },
  });

  const formik2 = useFormik({
    initialValues: {
      chamberNumber: chamberNumber ?? '',
      column: column ?? '',
      letter: letter ?? '',
      level: level ?? '',
    },
    validationSchema: locationSchema,
    onSubmit: (values, setSubmitting) => {
      return;
    },
  });

  const submit = async (remove: boolean): Promise<void> => {
    setIsSubmitting(true);

    const fromLocation = LocationFormikToLocationDto(formik1.values);
    if (remove) {
      const result = await InventoryApi.removeAllFromLocation(fromLocation);
      if (result.status === StatusCodes.OK) {
        onClose(formik1.values);
      }
    } else {
      const toLocation = LocationFormikToLocationDto(formik2.values);
      const result = await InventoryApi.moveAllFromLocation(fromLocation, toLocation);
      if (result.status === StatusCodes.NO_CONTENT) {
        onClose(formik1.values);
      }
    }
    setIsSubmitting(false);
    return;
  };

  useEffect(() => {
    if (chamberNumber && column && letter && level) {
      loadProducts({ chamberNumber, column, letter, level });
    }
  }, [chamberNumber, column, letter, level]);

  useEffect(() => {
    if (chamberNumber || column || letter || level) {
      formik1.setValues({
        chamberNumber: chamberNumber || '',
        column: column || '',
        letter: letter || '',
        level: level || '',
      });
    }
  }, [chamberNumber, column, letter, level]);

  return (
    <WhitePad isFluid>
      <TitleComponent onLeftHandler={history.goBack} titleLeft="Back" title="Change Location" />
      <Form>
        <Form.Row>
          <Col xs={6}>
            <LocationEditModal values={formik1.values} setField={formik1.setFieldValue} onClose={onClose} />
            {isSubmittingFrom && <h1>Loading...</h1>}
            {products?.map((item) => (
              <ProductBox item={item} key={uuid()} />
            ))}
            {!products && <h1>Choose Location to see list of products</h1>}
            {products && products.length === 0 && <h1>There are no products in this location</h1>}
          </Col>
          <Col xs={1}>{products && <Arrow>=={'>'}</Arrow>}</Col>
          <Col xs={5}>
            {products && (
              <>
                <PrimaryButton onClick={() => submit(true)} title="Remove" isSubmitting={isSubmitting} />
                or
                <LocationEditModal values={formik2.values} setField={formik2.setFieldValue} />
                {formik2.values.chamberNumber &&
                  formik2.values.column &&
                  formik2.values.letter &&
                  formik2.values.level && (
                    <PrimaryButton
                      onClick={() => submit(false)}
                      title="Move To"
                      variant="primary"
                      isSubmitting={isSubmitting}
                    />
                  )}
              </>
            )}
          </Col>
        </Form.Row>
      </Form>
    </WhitePad>
  );
}
